// A placed part's category fields — ONE definition per typed field, shown in its
// Properties tab and in the multi-selection panel: a heater's or a shower's body
// (its Height and colour), a heater's clearance gap and guard cover, a heated
// bench's or a salt wall's power. Which a part has is its category's traits
// (shared/categories.ts). The catalogue's editor lists a product's fields itself
// (productFields.ts). docs/claude/products.md.
import { traitsOf } from '../../shared/categories';
import { type ProductInstance } from '../../shared/types';
import {
  invalidate,
  pushHistory,
} from './core';
import {
  HEATER_BODY_COLOR,
  HEATER_COVER_COLOR,
  HEATER_GAP_DEFAULT,
} from '../geometry';
import { doorGlassSegments } from '../glass';
import {
  DEFAULT_GLASS_HEIGHT,
  objectHeight,
  partHeight,
} from '../heights';
import {
  colorRow,
  numberRow,
  sectionTitle,
} from './panelRows';
import {
  productOf,
  regenerateSauna,
  saunaOfHeater,
} from '../main';

interface CatField {
  id: string;
  section: string;
  label: string;
  kind: 'number' | 'color';
  /** Read and written in the label's own unit (mm for "(mm)", hex for colours). */
  iGet(inst: ProductInstance): number | string;
  iSet(inst: ProductInstance, v: number | string): void;
  /** After a placed-object edit, re-wrap a hosting sauna's benches. */
  regenSauna?: boolean;
  /** It is the part's own Height (writes `height`), so no generic Height row is
   *  added beside it and a multi-selection shares it with the others' Height. */
  setsHeight?: true;
}

const HEATER_SIDES = ['front', 'back', 'left', 'right'] as const;
const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const mmInt = (v: number | string): number => Math.max(0, Math.round(Number(v)));

/** Set/clear one side of a per-side thickness map (0 = no wall). */
export function setSide(map: { [k: string]: number } | undefined, side: string, v: number | string): { [k: string]: number } | undefined {
  const n = mmInt(v);
  const c = { ...(map ?? {}) };
  if (n > 0) c[side] = n; else delete c[side];
  return Object.keys(c).length ? c : undefined;
}

/** A floating body's fields — a heater's, a shower part's: its height and its
 *  colour. */
function bodyFields(): CatField[] {
  return [
    { id: 'height', section: 'Heater body', label: 'Height (mm)', kind: 'number', setsHeight: true,
      iGet: (i) => partHeight(i, productOf(i)), iSet: (i, v) => { i.height = Math.max(1, mmInt(v)); } },
    { id: 'bodyColor', section: 'Heater body', label: 'Body colour', kind: 'color',
      iGet: (i) => i.color ?? HEATER_BODY_COLOR, iSet: (i, v) => { i.color = String(v); } },
  ];
}

/** A guarded part's fields — a heater's: its clearance gap, then its guard
 *  cover (colour + per-side thickness). */
function guardFields(): CatField[] {
  const gap = HEATER_SIDES.map((side): CatField => ({
    id: `gap-${side}`, section: 'Heater gap', label: `${capFirst(side)} (mm)`, kind: 'number',
    iGet: (i) => i.heaterGap?.[side] ?? HEATER_GAP_DEFAULT, iSet: (i, v) => { i.heaterGap = { ...(i.heaterGap ?? {}), [side]: mmInt(v) }; },
    regenSauna: true,
  }));
  const colour: CatField = {
    id: 'coverColor', section: 'Heater cover', label: 'Cover colour', kind: 'color',
    iGet: (i) => i.heaterCoverColor ?? HEATER_COVER_COLOR, iSet: (i, v) => { i.heaterCoverColor = String(v); },
  };
  const sides = HEATER_SIDES.map((side): CatField => ({
    id: `cover-${side}`, section: 'Heater cover', label: `${capFirst(side)} (mm)`, kind: 'number',
    iGet: (i) => i.heaterCover?.[side] ?? 0, iSet: (i, v) => { i.heaterCover = setSide(i.heaterCover, side, v); },
    regenSauna: true,
  }));
  return [...gap, colour, ...sides];
}

function heatedBenchFields(): CatField[] {
  return [{
    id: 'powerPerMeter', section: 'Power', label: 'Power per running meter (W/m)', kind: 'number',
    iGet: (i) => i.powerPerMeter ?? 0, iSet: (i, v) => { i.powerPerMeter = Math.max(0, Math.round(Number(v))); },
  }];
}

function saltWallFields(): CatField[] {
  return [
    { id: 'saltHeight', section: 'Salt wall', label: 'Height (mm)', kind: 'number', setsHeight: true,
      iGet: (i) => objectHeight(i), iSet: (i, v) => { i.height = Math.max(1, mmInt(v)); } },
    { id: 'powerPerSqm', section: 'Power', label: 'Power per square meter (W/m²)', kind: 'number',
      iGet: (i) => i.powerPerSqm ?? 0, iSet: (i, v) => { i.powerPerSqm = Math.max(0, Math.round(Number(v))); } },
  ];
}

/** Sauna heater sizing: minimum heater power (kW) for a room up to a given
 *  effective volume (m³). This is an "up to" (fino a) table — the first row whose
 *  volume covers the value gives its power, so a value between two rows rounds up
 *  to the greater power. */
const SAUNA_POWER_TABLE: ReadonlyArray<{ maxVol: number; kw: number }> = [
  { maxVol: 6.35, kw: 4.5 }, { maxVol: 8.45, kw: 6 }, { maxVol: 10.2, kw: 7.5 },
  { maxVol: 14, kw: 9 }, { maxVol: 18, kw: 12 }, { maxVol: 25, kw: 15 },
  { maxVol: 30, kw: 18 }, { maxVol: 34, kw: 21 }, { maxVol: 40, kw: 24 },
  { maxVol: 50, kw: 27 }, { maxVol: 65, kw: 30 }, { maxVol: 75, kw: 36 },
  { maxVol: 80, kw: 42 }, { maxVol: 90, kw: 48 }, { maxVol: 100, kw: 54 },
  { maxVol: 130, kw: 60 }, { maxVol: 160, kw: 72 },
];

/** Heater power (kW) for an effective sauna volume (m³): the first table row whose
 *  volume covers it (rounding up to the greater); beyond the table, the maximum. */
export function saunaPowerKw(effectiveVolumeM3: number): number {
  for (const row of SAUNA_POWER_TABLE) if (effectiveVolumeM3 <= row.maxVol) return row.kw;
  return SAUNA_POWER_TABLE[SAUNA_POWER_TABLE.length - 1].kw;
}

/** Total glazed area (m²) of a sauna: its glass panes plus the (always-glass)
 *  door, each pane's length × the room's glass height. */
export function saunaGlassAreaM2(inst: ProductInstance): number {
  const h = inst.glassHeight ?? DEFAULT_GLASS_HEIGHT; // mm
  let lenMm = 0;
  for (const g of inst.glass || []) lenMm += Math.abs(g.end - g.start);
  for (const door of doorGlassSegments(inst)) lenMm += Math.abs(door.end - door.start);
  return (lenMm / 1000) * (h / 1000);
}

/** A placed part's category fields: a floating body's (a heater's, a shower
 *  part's), then a guarded one's gap and cover (a heater's), then a heated
 *  bench's or a salt wall's own; none for any other. */
export function categoryFields(category: string): CatField[] {
  const t = traitsOf(category);
  const own = category === 'heatedbench' ? heatedBenchFields() : category === 'saltwall' ? saltWallFields() : [];
  return [...(t.floatsBody ? bodyFields() : []), ...(t.guarded ? guardFields() : []), ...own];
}

/** A run (a heated or a sauna bench, a salt wall) whose category fields hold no
 *  Height of their own takes the generic Height row: the panel's and the
 *  multi-selection's one rule. */
export function runTakesHeightRow(category: string): boolean {
  return traitsOf(category).run && !categoryFields(category).some((f) => f.setsHeight);
}

/** Render a placed object's category fields into its Properties tab, grouped by
 *  section. */
export function appendCategoryFields(body: HTMLElement, inst: ProductInstance): void {
  let section = '';
  for (const f of categoryFields(inst.category)) {
    if (f.section !== section) { body.appendChild(sectionTitle(f.section)); section = f.section; }
    const val = f.iGet(inst);
    const afterEdit = (): void => { if (f.regenSauna) { const room = saunaOfHeater(inst); if (room) regenerateSauna(room); } };
    if (f.kind === 'color') {
      body.appendChild(colorRow(f.label, String(val), (hex) => { f.iSet(inst, hex); afterEdit(); }));
    } else {
      body.appendChild(numberRow(f.label, Number(val), (v) => { pushHistory(); f.iSet(inst, v); afterEdit(); invalidate(); }, { commit: true }));
    }
  }
}
